import * as React from 'react'

import { Button, ButtonProps } from '@ps/ds/components/primitives/Button/Button'
import { Heading, HeadingProps } from '@ps/ds/components/primitives/Heading/Heading'
import { cn } from '@ps/ds/lib/utils'
import {
  Empty as EmptyContainer,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from '@ps/ds/ui/empty'
import { cva } from 'class-variance-authority'

import emptyFallback from '../../../assets/illustrations/il-empty-bug-playing-guitar.svg?inline'

const emptyVariants = cva('flex flex-col items-center md:p-6 text-center', {
  variants: {
    variant: {
      default: 'gap-6',
      condensed: 'gap-4',
      fullWidth: 'gap-6 max-w-none',
    },
    size: {
      default: 'max-w-[400px]',
      large: 'max-w-[600px]',
    },
  },
  compoundVariants: [
    {
      variant: 'condensed',
      size: 'large',
      class: 'gap-5',
    },
  ],
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

const emptyMediaVariants = cva('w-full', {
  variants: {
    size: {
      default: 'max-w-[150px]',
      large: 'max-w-[350px]',
    },
  },
})

const emptyActionContentVariants = cva('flex flex-col items-center', {
  variants: {
    variant: {
      default: 'gap-6',
      condensed: 'gap-4',
      fullWidth: 'gap-6',
    },
    size: { large: '', default: '' },
  },
  compoundVariants: [
    {
      variant: 'condensed',
      size: 'large',
      class: 'gap-5',
    },
  ],
})

const emptyDescriptionVariants = cva(undefined, {
  variants: { size: { default: 'text-sm', large: 'text-base' } },
})

export type EmptyLinkAction = {
  label: React.ReactNode
  href: string
  target?: React.HTMLAttributeAnchorTarget
  rel?: string
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
}

export type EmptyButtonAction = Partial<Pick<EmptyLinkAction, 'href' | 'target' | 'rel'>> & {
  label: React.ReactNode
  onClick?: () => void
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  disabled?: boolean
}

export interface EmptyProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Empty component variant. */
  variant?: 'default' | 'condensed' | 'fullWidth'

  /** Empty component size */
  size?: 'default' | 'large'

  /** Image component. If omitted, a default/fallback is rendered. */
  image?: React.ReactNode

  /** Main heading, optional. */
  title?: string // optional

  /** Heading level, optional. Defaults to h3. */
  headingLevel?: HeadingProps['as']

  /** Supporting text, required. */
  description: string

  /** Optional action buttons. */
  primaryAction?: EmptyButtonAction
  secondaryAction?: EmptyButtonAction
  linkAction?: EmptyLinkAction
}

const EmptyAction = ({
  action,
  variant,
}: {
  action: EmptyButtonAction & { variant?: ButtonProps['variant'] }
  variant: EmptyProps['variant']
}) => {
  const buttonSize = variant === 'condensed' ? 'sm' : 'md'

  const ctaProps: ButtonProps = {
    variant: action.variant ?? 'primary',
    onClick: action.onClick,
    leadingIcon: action.leadingIcon,
    trailingIcon: action.trailingIcon,
    disabled: action.disabled,
    className: 'flex-grow min-w-min',
    size: buttonSize,
  }

  if (action.href) {
    return (
      <Button {...ctaProps} asChild>
        <a href={action.href} target={action.target} rel={action.rel}>
          {action.label}
        </a>
      </Button>
    )
  }

  return <Button {...ctaProps}>{action.label}</Button>
}

export const Empty = ({
  variant = 'default',
  size = 'default',
  image,
  title,
  headingLevel = 'h3',
  description,
  primaryAction,
  secondaryAction,
  linkAction,
  className,
}: EmptyProps) => {
  const buttonSize = variant === 'condensed' ? 'sm' : 'md'
  const hasActions = !!primaryAction || !!secondaryAction || !!linkAction

  return (
    <EmptyContainer data-testid="empty" className={cn(emptyVariants({ variant, size }), className)}>
      <EmptyHeader>
        <EmptyMedia className={cn(emptyMediaVariants({ size }))}>
          {image ? image : <img src={emptyFallback} className="w-full" alt="" aria-hidden="true" />}
        </EmptyMedia>
        {title && (
          <Heading
            size={size === 'default' ? 'lg' : '2xl'}
            weight={size === 'default' ? 'semibold' : 'bold'}
            as={headingLevel}>
            {title}
          </Heading>
        )}
        <EmptyDescription className={emptyDescriptionVariants({ size })}>
          {description}
        </EmptyDescription>
      </EmptyHeader>

      {hasActions && (
        <EmptyContent className={emptyActionContentVariants({ variant, size })}>
          <div className="flex flex-wrap gap-2 justify-center">
            {primaryAction && <EmptyAction action={primaryAction} variant={variant} />}
            {secondaryAction && (
              <EmptyAction
                action={{ ...secondaryAction, variant: 'secondary' }}
                variant={variant}
              />
            )}
          </div>

          {linkAction && (
            <Button
              variant="link"
              leadingIcon={linkAction.leadingIcon}
              trailingIcon={linkAction.trailingIcon}
              asChild
              size={buttonSize}>
              <a
                className="text-center underline"
                href={linkAction.href}
                target={linkAction.target}
                rel={
                  linkAction.rel ??
                  (linkAction.target === '_blank' ? 'noopener noreferrer' : undefined)
                }>
                {linkAction.label}
              </a>
            </Button>
          )}
        </EmptyContent>
      )}
    </EmptyContainer>
  )
}
